// Add-to-calendar support for cohort course sessions — generates a data: ICS
// URI, fully prerender-safe (pure string building, no APIs). Also computes
// next-occurrence and timezone-local labels for the recurring free session.
import type { CohortCourse } from '$lib/types/cohort-course';

const MINUTES_PER_HOUR = 60;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const;
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Cohort schedule block (startDate/days/time/zone) from a CohortCourse. */
export type CohortSchedule = CohortCourse['cohort'];

/**
 * Deterministic date label ("Nov 10, 2026") — no toLocaleDateString, so
 * prerendered output never disagrees with client hydration on locale.
 */
export function formatCourseDate(iso: string): string {
	const [y, m, d] = iso.split('-').map(Number);
	return `${MONTHS[m - 1]} ${d}, ${y}`;
}

/** Wall-clock session start parsed from timeLabel ("7:00 PM IST" → [19, 0]). */
export function parseSessionTime(timeLabel: string): [number, number] {
	const match = timeLabel.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
	if (!match) return [19, 0];
	const hour = (parseInt(match[1], 10) % 12) + (match[3].toUpperCase() === 'PM' ? 12 : 0);
	return [hour, parseInt(match[2], 10)];
}

function offsetToMinutes(offset: string): number {
	const sign = offset.startsWith('-') ? -1 : 1;
	const [oh, om] = offset.replace(/^[+-]/, '').split(':').map(Number);
	return sign * (oh * 60 + om);
}

/** "Mon & Wed" style list of a cohort's session days. */
export function sessionDayList(cohort: CohortSchedule): string {
	return cohort.sessionDays
		.map((code) => WEEKDAY_SHORT[WEEKDAY_CODES.indexOf(code)])
		.join(' & ');
}

function anchorFromIso(iso: string): Date {
	return new Date(`${iso}T00:00:00Z`);
}

/**
 * Next session start at or after `anchor`, as a UTC instant. Deterministic
 * when anchored to the cohort's startDate (prerendered fallbacks); anchored
 * to now it always yields the genuinely next session (client upgrade).
 * Sessions recur on sessionDays at the timeLabel wall clock in the cohort's
 * fixed-offset home timezone (IST has no DST, so pure calendar math is safe).
 */
export function nextSessionStart(cohort: CohortSchedule, anchor: Date = new Date()): Date | null {
	const [hour, minute] = parseSessionTime(cohort.timeLabel);
	const offsetMin = offsetToMinutes(cohort.timezoneOffset);
	const wanted = new Set(cohort.sessionDays.map((code) => WEEKDAY_CODES.indexOf(code)));
	// Same instant expressed in home wall clock, then walk home-calendar days.
	const home = new Date(anchor.getTime() + offsetMin * 60000);
	const y = home.getUTCFullYear();
	const m = home.getUTCMonth();
	const d = home.getUTCDate();
	for (let i = 0; i < 15; i++) {
		const day = new Date(Date.UTC(y, m, d + i));
		if (!wanted.has(day.getUTCDay())) continue;
		const sessionUtc = Date.UTC(y, m, d + i, hour, minute) - offsetMin * 60000;
		if (sessionUtc >= anchor.getTime()) return new Date(sessionUtc);
	}
	return null;
}

export type SessionLabelVariant = 'date' | 'time' | 'full';

/**
 * Session label in the cohort's home timezone, anchored to startDate —
 * string-built (no Intl) so prerender and hydration always agree.
 * "Mon, Oct 19" / "7:00 PM IST" / "Mon, Oct 19, 7:00 PM IST".
 */
export function formatSessionLabel(cohort: CohortSchedule, variant: SessionLabelVariant): string {
	const start = nextSessionStart(cohort, anchorFromIso(cohort.startDate));
	if (!start) return formatCourseDate(cohort.startDate);
	const home = new Date(start.getTime() + offsetToMinutes(cohort.timezoneOffset) * 60000);
	const dateLabel = `${WEEKDAY_SHORT[home.getUTCDay()]}, ${MONTHS[home.getUTCMonth()]} ${home.getUTCDate()}`;
	if (variant === 'date') return dateLabel;
	if (variant === 'time') return cohort.timeLabel;
	return `${dateLabel}, ${cohort.timeLabel}`;
}

/**
 * Session label in the visitor's timezone, anchored to now — client-only
 * (mirrors the currency selector's locale detection); null keeps the
 * prerendered home-timezone fallback. Uses Intl with an explicit locale and
 * zone, so it is deterministic given the visitor's zone.
 */
export function formatSessionLocalLabel(
	cohort: CohortSchedule,
	variant: SessionLabelVariant
): string | null {
	if (typeof window === 'undefined') return null;
	let timeZone: string;
	try {
		timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	} catch {
		return null;
	}
	const start = nextSessionStart(cohort);
	if (!start) return null;
	const options: Intl.DateTimeFormatOptions =
		variant === 'date'
			? { weekday: 'short', month: 'short', day: 'numeric', timeZone }
			: variant === 'time'
				? { hour: 'numeric', minute: '2-digit', timeZoneName: 'short', timeZone }
				: {
						weekday: 'short',
						month: 'short',
						day: 'numeric',
						hour: 'numeric',
						minute: '2-digit',
						timeZoneName: 'short',
						timeZone
					};
	try {
		return new Intl.DateTimeFormat('en-US', options).format(start);
	} catch {
		return null;
	}
}

function toIcsStamp(date: Date): string {
	return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
}

function parseSessionMinutes(length: string): number {
	const match = length.match(/(\d+(?:\.\d+)?)/);
	return match ? Math.round(parseFloat(match[1])) : MINUTES_PER_HOUR;
}

/**
 * Builds a `data:text/calendar` URI covering a course's session series.
 * Single occurrence → one VEVENT; recurring cohorts → weekly RRULE over the
 * session days with COUNT = weeks × days/week. DTSTART is the first session
 * on/after startDate (so a non-session-day startDate can't desync the RRULE).
 */
export function buildCourseIcs(course: CohortCourse): string {
	const startUtc =
		nextSessionStart(course.cohort, anchorFromIso(course.cohort.startDate)) ??
		anchorFromIso(course.cohort.startDate);
	const endUtc = new Date(
		startUtc.getTime() + parseSessionMinutes(course.cohort.sessionLength) * 60 * 1000
	);

	const count = course.cohort.weeks * course.cohort.sessionDays.length;
	const rrule =
		count > 1
			? [`RRULE:FREQ=WEEKLY;BYDAY=${course.cohort.sessionDays.join(',')};COUNT=${count}`]
			: [];

	const ics = [
		'BEGIN:VCALENDAR',
		'VERSION:2.0',
		'PRODID:-//Akonia Codex//Cohort Courses//EN',
		'BEGIN:VEVENT',
		`UID:${course.slug}-${startUtc.getTime()}@akoniacodex.com`,
		`DTSTAMP:${toIcsStamp(new Date())}`,
		`DTSTART:${toIcsStamp(startUtc)}`,
		`DTEND:${toIcsStamp(endUtc)}`,
		...rrule,
		`SUMMARY:${course.title} — Akonia Codex`,
		`DESCRIPTION:${course.cohort.cadence}. ${course.cohort.timeLabel}.`,
		'END:VEVENT',
		'END:VCALENDAR'
	].join('\r\n');

	return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
}
